import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CalendarClock,
  Loader2,
  MicVocal,
  Pause,
  Pencil,
  Play,
  Save,
  Search,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuthStore } from '@/lib/auth/store';
import { cn } from '@/lib/utils/cn';
import { useDiscardTts, useFinalizeTts, useTtsHistory } from '../api';
import { sortTtsNewestFirst } from '../sort';
import {
  CATEGORY_LABEL,
  type PromptCategory,
  type TtsHistoryRow,
} from '../types';
import { Pagination } from './Pagination';
import { TtsEditDialog } from './TtsEditDialog';

interface Props {
  onSuccess?: () => void;
  /** When true, render a top search bar + bottom pagination. Off by default
   *  because the panel ships inside a slim slide-over where the parent
   *  controls layout. */
  paginated?: boolean;
}

function statusBadge(status: number | string): {
  label: string;
  className: string;
} {
  const n = Number(status);
  if (n === 0)
    return { label: 'Pending', className: 'bg-amber-100 text-amber-900' };
  if (n === 1)
    return { label: 'Saved', className: 'bg-emerald-100 text-emerald-900' };
  if (n === 2)
    return { label: 'Rejected', className: 'bg-rose-100 text-rose-900' };
  return { label: 'Deleted', className: 'bg-slate-200 text-slate-900' };
}

export function TtsHistoryPanel({ onSuccess, paginated = false }: Props) {
  const userId = useAuthStore((s) => s.userId);
  const history = useTtsHistory();
  const finalize = useFinalizeTts();
  const discard = useDiscardTts();

  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  /* ---- One-shot playback coordinator ---- */
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playingId, setPlayingId] = useState<number | null>(null);

  /* ---- Edit dialog target ---- */
  const [editTarget, setEditTarget] = useState<TtsHistoryRow | null>(null);

  const stop = () => {
    audioRef.current?.pause();
    audioRef.current = null;
    setPlayingId(null);
  };

  const togglePlay = (row: TtsHistoryRow) => {
    if (!row.downloadUrl) {
      toast.error('No audio URL for this row.');
      return;
    }
    if (playingId === row.id) {
      stop();
      return;
    }
    stop();
    const a = new Audio(row.downloadUrl);
    a.addEventListener('ended', () => setPlayingId(null));
    void a.play();
    audioRef.current = a;
    setPlayingId(row.id);
  };

  const filtered = useMemo(() => {
    const all = sortTtsNewestFirst(history.data ?? []);
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (r) =>
        (r.filename ?? '').toLowerCase().includes(q) ||
        (r.textScript ?? '').toLowerCase().includes(q) ||
        (r.language ?? '').toLowerCase().includes(q) ||
        (r.category ?? '').toLowerCase().includes(q) ||
        (r.userName ?? '').toLowerCase().includes(q),
    );
  }, [history.data, query]);

  useEffect(() => {
    setPage(1);
  }, [query, pageSize]);

  const rows = useMemo(
    () =>
      paginated
        ? filtered.slice((page - 1) * pageSize, page * pageSize)
        : filtered,
    [filtered, page, pageSize, paginated],
  );

  const handleSave = (row: TtsHistoryRow) => {
    if (!row.downloadUrl) {
      toast.error('Cannot finalize without an audio URL.');
      return;
    }
    finalize.mutate(
      {
        id: row.id,
        userId: Number(userId),
        status: 1,
        downloadUrl: row.downloadUrl,
        promptName: row.filename,
        promptDuration: row.promptDuration ?? 0,
        characterCount: row.characterCount,
        category: row.category as PromptCategory,
      },
      {
        onSuccess: (res) => {
          toast.success(res?.message ?? 'Saved to library');
          onSuccess?.();
        },
        onError: () => toast.error('Could not save the prompt.'),
      },
    );
  };

  const handleDiscard = (row: TtsHistoryRow) => {
    discard.mutate(row.id, {
      onSuccess: () => toast.success('Discarded'),
      onError: () => toast.error('Could not discard.'),
    });
  };

  const SearchBar = paginated ? (
    <div className="relative">
      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
      <Input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search by filename, script, language…"
        className="h-9 pl-7 text-sm"
      />
    </div>
  ) : null;

  if (history.isLoading) {
    return (
      <div className="space-y-3">
        {SearchBar}
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="flex items-center gap-3 border-b border-border last:border-b-0 px-3 py-2.5"
            >
              <Skeleton className="h-7 w-7 rounded-full" />
              <Skeleton className="h-3 flex-1 max-w-sm" />
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-3 w-24" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="space-y-3">
        {SearchBar}
        <div className="rounded-lg border border-dashed border-border bg-card/50 py-10 text-center">
          <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <MicVocal className="h-5 w-5" />
          </div>
          <div className="text-sm font-semibold text-foreground">
            {query ? 'No matches' : 'No TTS history'}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {query
              ? 'Try clearing the search.'
              : 'Synthesize from text to see your previous renders here.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {SearchBar}
      <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
        {rows.map((r) => {
          const status = statusBadge(r.status);
          const isPending = Number(r.status) === 0;
          return (
            <div
              key={r.id}
              className={cn(
                'group/row flex items-start gap-3 border-b border-border px-3 py-2.5 transition-colors last:border-b-0 hover:bg-muted/30',
                playingId === r.id && 'bg-primary/5',
              )}
            >
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className={cn(
                  'mt-0.5 h-8 w-8 shrink-0 rounded-full',
                  playingId === r.id &&
                    'bg-primary text-primary-foreground hover:bg-primary',
                )}
                onClick={() => togglePlay(r)}
                disabled={!r.downloadUrl}
                aria-label="Play TTS row"
              >
                {playingId === r.id ? (
                  <Pause className="h-3.5 w-3.5" />
                ) : (
                  <Play className="h-3.5 w-3.5" />
                )}
              </Button>

              <div className="min-w-0 flex-1">
                {/* Title row: filename + status + category */}
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="truncate text-sm font-semibold text-foreground">
                    {r.filename}
                  </span>
                  <Badge
                    variant="outline"
                    className={cn(
                      'px-1.5 py-0 text-[10px]',
                      status.className,
                    )}
                  >
                    {status.label}
                  </Badge>
                  <Badge
                    variant="outline"
                    className="px-1.5 py-0 text-[10px]"
                  >
                    {CATEGORY_LABEL[r.category as PromptCategory] ??
                      r.category}
                  </Badge>
                </div>
                {/* TTS script — main scannable content */}
                <p className="mt-1 line-clamp-2 text-xs text-foreground/80">
                  {r.textScript}
                </p>
                {/* Meta row */}
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                  <span>
                    {r.language}
                    <span className="text-muted-foreground/70">
                      {' '}
                      · {r.gender}
                    </span>
                  </span>
                  <span>{r.characterCount} chars</span>
                  {r.reqDate && (
                    <span className="flex items-center gap-1 font-mono">
                      <CalendarClock className="h-3 w-3" />
                      {r.reqDate.slice(0, 16)}
                    </span>
                  )}
                </div>
              </div>

              {!isPending && Number(r.status) === 1 && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7 shrink-0 px-2 text-xs"
                  onClick={() => setEditTarget(r)}
                  aria-label="Edit TTS clip"
                >
                  <Pencil className="h-3.5 w-3.5" />
                  Edit
                </Button>
              )}

              {isPending && (
                <div className="flex shrink-0 flex-col gap-1">
                  <Button
                    type="button"
                    size="sm"
                    variant="default"
                    className="h-7 px-2 text-xs"
                    onClick={() => handleSave(r)}
                    disabled={finalize.isPending}
                  >
                    {finalize.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Save className="h-3.5 w-3.5" />
                    )}
                    Save
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-xs text-destructive hover:text-destructive"
                    onClick={() => handleDiscard(r)}
                    disabled={discard.isPending}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Discard
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {paginated && filtered.length > 0 && (
        <Pagination
          page={page}
          pageSize={pageSize}
          total={filtered.length}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      )}

      <TtsEditDialog
        open={editTarget !== null}
        onOpenChange={(o) => !o && setEditTarget(null)}
        row={editTarget}
      />
    </div>
  );
}
